import React from 'react';
import { useIntl } from 'gatsby-plugin-react-intl';

import BaseCard from '@prospectus/common/ui-strict/BaseCard';
import ExpandedProductCard from '@prospectus/common/ui-strict/ExpandedProductCard';
import { createCodeFriendlyProduct } from '@prospectus/common/types/productTypes';
import {
  getAnalytics,
  getFinalUrl,
  getVariant,
} from '@prospectus/common/ui/ProductCard/utils';
import { EXTERNAL_PRODUCT_SOURCES } from '@utils/discoveryConstants';

import Footer from './components/Footer';
import { ProductCardProps, ProductCardDefaultProps } from './types';
import { cardBadgesCopy, cardFooterCopy } from './messages';

const getLearningTypeString = (levelType, pacingType) => {
  const pacingTypeMap = {
    self_paced: 'Self-paced',
    instructor_paced: 'Instructor-paced',
  };

  const pacingTypeString = pacingTypeMap[pacingType];

  if (pacingTypeString && levelType) {
    return `${pacingTypeString}, ${levelType.toLowerCase()} level`;
  }

  if (pacingTypeString) {
    return pacingTypeString;
  }

  if (levelType) {
    return `${levelType} level`;
  }

  return '';
};

function ProductCard(props: ProductCardProps) {
  const {
    headerImage,
    schoolLogo,
    title,
    schoolName,
    footerMessageKey,
    productType,
    handleOnClick,
    isLoading,
    owners,
    uuid,
    shouldfirePreQuerySegmentEvent,
    useExpandedProductCard,
    weeksToComplete,
    minPrice,
    maxPrice,
    courseCount,
    levelType,
    pacingType,
    useCohesion,
    cohesionPayload,
  } = props;
  const intl = useIntl();

  const footerMessagesObj = {
    [EXTERNAL_PRODUCT_SOURCES.EMERITUS]: intl.formatMessage(
      cardFooterCopy['prospectus.2u-product-card.footer-text.emeritus'],
    ),
    [EXTERNAL_PRODUCT_SOURCES.SHORELIGHT]: intl.formatMessage(
      cardFooterCopy['prospectus.2u-product-card.footer-text.shorelight'],
    ),
  };

  const finalUrl = getFinalUrl(props);

  // Get card badge copy
  const productTypeCopy = intl.formatMessage(
    cardBadgesCopy[
      `prospectus.2u-product-card.pill-text.${createCodeFriendlyProduct(productType)}`
    ],
  );

  // Get analytics
  const analytics = getAnalytics(props);

  // Get variant
  const variant = getVariant(productType);

  // Hide school logo when there are several institutions as owners
  const multipleSchoolName = [];
  const isMultipleOwner = owners?.length > 1;
  if ((owners?.length > 1)) {
    owners.forEach((owner, index, arr) => {
      let school;
      if (index === arr.length - 1) {
        school = (
          <span>{owner.name}</span>
        );
      } else {
        school = (
          <>
            <span>{owner.name}</span>
            <br />
          </>
        );
      }

      multipleSchoolName.push(school);
    });
  }

  if (useExpandedProductCard) {
    return (
      <ExpandedProductCard
        url={finalUrl}
        cardAnalytics={analytics}
        customHeaderImage={headerImage}
        schoolLogo={isMultipleOwner ? '' : schoolLogo}
        title={title}
        subtitle={isMultipleOwner ? multipleSchoolName : schoolName}
        variant={variant}
        productTypeCopy={productTypeCopy}
        uuid={uuid}
        minPrice={minPrice}
        maxPrice={maxPrice}
        length={weeksToComplete ? `${weeksToComplete} weeks` : ''}
        learningType={getLearningTypeString(levelType, pacingType)}
        courseCount={courseCount}
      />
    );
  }

  return (
    <BaseCard
      url={finalUrl}
      customHeaderImage={headerImage}
      schoolLogo={isMultipleOwner ? '' : schoolLogo}
      title={title}
      uuid={uuid}
      subtitle={isMultipleOwner ? multipleSchoolName : schoolName}
      productTypeCopy={productTypeCopy}
      variant={variant}
      cardAnalytics={analytics}
      footer={<Footer {...props} footerMessage={footerMessagesObj?.[footerMessageKey]} />}
      handleOnClick={handleOnClick}
      isLoading={isLoading}
      shouldfirePreQuerySegmentEvent={shouldfirePreQuerySegmentEvent}
      useCohesion={useCohesion}
      cohesionPayload={cohesionPayload}
    />
  );
}

ProductCard.defaultProps = ProductCardDefaultProps;

export default ProductCard;
